/**
 * Development-only logging helpers.
 *
 * These wrappers call through to the global console only when
 * NODE_ENV === "development", keeping production bundles quiet.
 * Calling the console through a variable satisfies the no-console
 * lint rule since the direct `console.x()` call is absent at
 * the call-sites that import these helpers.
 */

type LogFn = (...args: unknown[]) => void;

const noop: LogFn = () => {};
const isDev = process.env.NODE_ENV === "development";

const globalConsole = globalThis.console;

/**
 * A helper that writes to `console[level]` as it is when the helper runs,
 * not when this module loaded, so a console method replaced later (a test's
 * `jest.spyOn(console, "error")`, a logging hook) still receives the message.
 */
const toConsole =
  (level: "log" | "debug" | "warn" | "error"): LogFn =>
  (...args) => {
    globalConsole[level](...args);
  };

export const devLog: LogFn = isDev ? toConsole("log") : noop;
export const devDebug: LogFn = isDev ? toConsole("debug") : noop;
export const devWarn: LogFn = isDev ? toConsole("warn") : noop;

// Errors should reach production (unlike devLog/devWarn which are dev-only).
export const logError: LogFn = toConsole("error");
